import { toString } from '@contact/upgrade';

/**
 * Map of T
 */
export interface EntityState<T> {
  readonly ids: string[];
  readonly entities: { [id: string]: T };
}

/**
 * Utility interface to update entity
 */
export interface Update<T> {
  readonly id: string;
  readonly changes: Partial<T>;
}

/**
 * Utility method to create update object
 */
export function toUpdate<T>(id: any, changes: Partial<T>): Update<T> {
  return { id: toString(id), changes };
}

/**
 * Generic adapter to store enities definition
 */
export interface IAdapter<T, S> {
  addOne(value: T, state: S): S;
  updateOne(value: Update<T>, state: S): S;
  removeOne(id: string, state: S): S;
  addMany(values: T[], state: S): S;
  updateMany(values: Update<T>[], state: S): S;
  removeMany(ids: string[], state: S): S;
}

/**
 * Abstract adapter extends this to create new adapter
 */
export abstract class AbstractAdapter<T, S> implements IAdapter<T, S> {
  constructor(protected readonly toId: (value: T) => string, protected readonly compare?: (v1: T, v2: T) => number) {}

  abstract getInitialState(extraData?: any): S;

  abstract addOne(value: T, state: S): S;
  abstract updateOne(value: Update<T>, state: S): S;
  abstract removeOne(id: string, state: S): S;

  addMany(values: T[], state: S): S {
    return values.reduce<S>((s, value) => this.addOne(value, s), state);
  }

  updateMany(values: Update<T>[], state: S): S {
    return values.reduce<S>((s, value) => this.updateOne(value, s), state);
  }

  removeMany(ids: string[], state: S): S {
    return ids.reduce<S>((s, id) => this.removeOne(id, s), state);
  }
}

/**
 * Adapter for array structure store
 */
export class ArrayAdapter<T> extends AbstractAdapter<T, Array<T>> {
  getInitialState(): Array<T> {
    return [];
  }

  addOne(value: T, state: Array<T>): Array<T> {
    const id = this.toId(value);
    let newState = [...state.filter(e => this.toId(e) !== id), value];
    if (this.compare) {
      return newState.sort(this.compare);
    }
    return newState;
  }

  updateOne(value: Update<T>, state: Array<T>): Array<T> {
    let id = value.id;
    let previous = state.filter(e => this.toId(e) === id).shift();
    if (!previous) {
      return state;
    }
    let newState = [
      ...state.filter(e => this.toId(e) !== id),
      {
        ...previous,
        ...value.changes,
      },
    ];
    if (this.compare) {
      return newState.sort(this.compare);
    }
    return newState;
  }

  removeOne(id: string, state: Array<T>): Array<T> {
    return state.filter(e => this.toId(e) !== id);
  }
}

/**
 * Adapter for json structure store
 */
export class EntitiesAdapter<T> extends AbstractAdapter<T, EntityState<T>> {
  private ADAPTER = new ArrayAdapter<string>(id => id);

  constructor(protected override readonly toId: (value: T) => string) {
    super(toId);
  }

  getInitialState<M extends EntityState<T>>(extraData?: M): M {
    return {
      ...(extraData || {}),
      // initial value structure
      ids: new Array<string>(),
      entities: {},
    } as M;
  }

  addOne(value: T, state: EntityState<T>): EntityState<T> {
    return {
      ...state,
      ids: this.ADAPTER.addOne(this.toId(value), state.ids),
      entities: {
        ...state.entities,
        [this.toId(value)]: value,
      },
    };
  }

  updateOne(value: Update<T>, state: EntityState<T>): EntityState<T> {
    let id = value.id;
    return {
      ...state,
      entities: {
        ...state.entities,
        // update element with given id
        [id]: {
          ...state.entities[id],
          ...value.changes,
        },
      },
    };
  }

  removeOne(id: string, state: EntityState<T>): EntityState<T> {
    const { [id]: removed, ...rest } = state.entities;
    return {
      ...state,
      ids: this.ADAPTER.removeOne(id, state.ids),
      entities: rest,
    };
  }
}
